import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ClientInformation from "./steps/BookingClientInformation";
import ServiceInformation from "./steps/BookingServiceDetails";
import PackageSelection from "./steps/AddBookingPackageSelection";
import Confirmation from "./steps/AddBookingConfirmation";
import { BookingData, LocationState } from "../../types/bookings";
import { StepConfig } from "../../types/steps";
import CustomStepper from "../../components/ui/Stepper";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import SiteDetails from "./steps/SiteDetails";
import { toast } from "react-toastify";
import apiService from "../../service/Api";
import { checkPermission } from "../../service/permissionGuard";
import { PERMISSIONS } from "../../constants/permissions";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import { BookingResponse } from "./types/add-booking";
import ConferenceBookingForm from "./components/ConferenceBookingForm";
import { buildBookingFormData } from "./utils/add-booking-form-data";

const AddBooking = () => {
  const [loading] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { serviceType, prefillData } = (location.state as LocationState) || {
    serviceType: "OTHER",
  };
  const [activeStep, setActiveStep] = useState<number>(0);
  const [bookingData, setBookingData] = useState<BookingData>(
    prefillData || initialValues,
  );
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState<boolean | null>(
    null,
  );
  const [bookingId, setBookingId] = useState<string | null>(null);

  useEffect(() => {
    if (prefillData) {
      setBookingData(prefillData);
      return;
    }
    setBookingData((prev) => ({
      ...prev,
      booking_type: prev.booking_type || serviceType,
    }));
  }, [serviceType, prefillData]);

  const stepsConfig: Record<string, StepConfig[]> = {
    EVENT: [
      { label: "Client Information" },
      { label: "Event Details" },
      { label: "Package Selection" },
      { label: "Confirmation" },
    ],
    CONFERENCE: [
      { label: "Client Information" },
      { label: "Conference Details" },
      { label: "Package Selection" },
      { label: "Confirmation" },
    ],
    FUNERAL: [
      { label: "Client Information" },
      { label: "Service Details" },
      { label: "Package Selection" },
      { label: "Confirmation" },
    ],
    CHURCH: [
      { label: "Client Information" },
      { label: "Service Details" },
      { label: "Package Selection" },
      { label: "Confirmation" },
    ],
    OTHER: [
      { label: "Client Information" },
      { label: "Service Details" },
      { label: "Package Selection" },
      { label: "Confirmation" },
    ],
    FUNERAL_HOME: [
      { label: "Client Information" },
      { label: "Service Details" },
      { label: "Package Selection" },
      { label: "Confirmation" },
    ],
  };

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const steps: StepConfig[] = stepsConfig[serviceType] || [
    { label: "Client Information" },
    { label: "Service Details" },
    { label: "Package Selection" },
    { label: "Confirmation" },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setBookingData(initialValues);
    setSubmissionSuccess(null);
    setSubmissionError(null);
    setBookingId(null);
  };

  const submitBooking = async (dataToSubmit: BookingData) => {
    setSubmitting(true);
    setSubmissionError(null);
    setSubmissionSuccess(null);
    try {
      checkPermission(PERMISSIONS.BOOKING_ADD);
      const formData = buildBookingFormData(dataToSubmit);

      const response = await apiService.post<BookingResponse>(
        "/api/bookings/booking/create-fullbooking/",
        formData,
      );

      setBookingId(response.data.id.toString());
      setSubmissionSuccess(true);

      toast.success(`Booking has been created successfully`, {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });

      setTimeout(() => {
        navigate(`/bookings/${response.data.id}`);
      }, 2000);
    } catch (err: unknown) {
      let errorMessage = "Failed to create booking.";
      const responseData = (
        err as {
          response?: { status?: number; data?: Record<string, unknown> };
        }
      )?.response;
      if (responseData?.status === 401) {
        navigate("/login");
        errorMessage = "Session expired. Please log in.";
      } else if ((responseData?.data as { detail?: string })?.detail) {
        errorMessage =
          (responseData?.data as { detail?: string }).detail || errorMessage;
      } else if (responseData?.data) {
        try {
          errorMessage = Object.entries(responseData.data)
            .map(([key, value]) =>
              Array.isArray(value)
                ? `${key}: ${value.join(", ")}`
                : `${key}: ${value}`,
            )
            .join("; ");
        } catch {
          errorMessage = JSON.stringify(responseData.data);
        }
      }

      setSubmissionError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });

      const errorLog = err as {
        response?: { data?: unknown };
        message?: string;
      };
      console.error(
        "Submission error:",
        errorLog.response?.data || errorLog.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getStepContent = (stepIndex: number) => {
    const stepLabel = steps[stepIndex]?.label;
    switch (stepLabel) {
      case "Client Information":
        return (
          <ClientInformation
            serviceType={serviceType}
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case "Service Details":
      case "Event Details":
        return (
          <ServiceInformation
            serviceType={serviceType}
            bookingData={bookingData}
            setBookingData={setBookingData}
          />
        );
      case "Conference Details":
        return (
          <>
            <ConferenceBookingForm
              bookingData={bookingData}
              setBookingData={setBookingData}
              serviceType={serviceType}
            />
            <SiteDetails
              bookingData={bookingData}
              setBookingData={setBookingData}
              serviceType={serviceType}
            />
          </>
        );

      case "Package Selection":
        return (
          <PackageSelection
            serviceType={serviceType}
            setBookingData={setBookingData}
            bookingData={bookingData}
          />
        );
      case "Confirmation":
        return (
          <Confirmation
            serviceType={serviceType}
            bookingData={bookingData}
            goToStep={(index) => setActiveStep(index)}
          />
        );
      default:
        return <Typography>Unknown step: {steps[stepIndex]?.label}</Typography>;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress sx={{ color: reduxTheme.primary_colors[800] }} />
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        p: 2,
        pt: 3,
        mb: 3,
        borderRadius: 2,
        minHeight: "80vh",
        position: "relative",
        boxShadow: 0,
      }}
    >
      <CustomStepper
        steps={steps.map((s) => s.label)}
        activeStep={activeStep}
        onStepClick={(index) => setActiveStep(index)}
      />

      <Box minHeight={30}></Box>
      <div>
        {activeStep === steps.length ? (
          <Box sx={{ mt: 2, mb: 1, textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              Booking Submission Status
            </Typography>
            {submitting && <CircularProgress />}
            {submissionSuccess && (
              <Typography color="success" variant="body1">
                Booking submitted successfully!
              </Typography>
            )}
            {submissionError && (
              <Typography color="error" variant="body1">
                {submissionError}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                pt: 2,
                justifyContent: "center",
              }}
            >
              <Button onClick={handleReset} variant="outlined">
                Start New Booking
              </Button>
              {bookingId && submissionSuccess && (
                <Button
                  onClick={() => navigate(`/bookings/${bookingId}`)}
                  variant="contained"
                  sx={{ ml: 2 }}
                >
                  View Booking Details
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <>
            {getStepContent(activeStep)}

            {/* Sticky Buttons */}
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "background.paper",
                display: "flex",
                flexDirection: "row",
                zIndex: 1,
                justifyContent: "space-between",
                mt: 3,
                p: 2,
              }}
            >
              <Button
                startIcon={<KeyboardArrowLeft />}
                sx={{
                  backgroundColor: reduxTheme.primary_colors[800],
                  pr: 5,
                  pl: 5,
                  mr: 1,
                  pt: 1,
                  pb: 1,
                  color: reduxTheme.primary_colors[100],
                  "&:hover": {
                    backgroundColor: reduxTheme.primary_colors[400],
                    color: reduxTheme.primary_colors[900],
                  },
                }}
                disabled={activeStep === 0 || submitting}
                onClick={handleBack}
              >
                Previous
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              {activeStep < steps.length - 1 ? (
                <Button
                  sx={{
                    backgroundColor: reduxTheme.primary_colors[400],
                    pr: 5,
                    pl: 5,
                    mr: 1,
                    pt: 1,
                    pb: 1,
                    color: "white",
                  }}
                  onClick={handleNext}
                  endIcon={<KeyboardArrowRight />}
                  disabled={submitting}
                >
                  Next
                </Button>
              ) : (
                <Button
                  sx={{
                    backgroundColor: reduxTheme.primary_colors[400],
                    pr: 5,
                    pl: 5,
                    mr: 1,
                    pt: 1,
                    pb: 1,
                    color: "white",
                    "&:hover": {
                      backgroundColor: reduxTheme.primary_colors[300],
                    },
                  }}
                  onClick={() => submitBooking(bookingData)}
                  disabled={submitting}
                  startIcon={
                    submitting && <CircularProgress size={20} color="inherit" />
                  }
                >
                  {submitting ? "Submitting..." : "Submit Booking"}
                </Button>
              )}
            </Box>
          </>
        )}
      </div>
    </Paper>
  );
};

export const initialValues: BookingData = {
  first_name: "",
  middlename: null,
  client_salutation: "",
  source_of_booking: "",
  deceased_photo_url: null,
  surname: null,
  marital_status: "",
  email: null,
  dob: null,
  customer_address: null,
  occupation: null,
  passport_number: "",
  nationality: "Zimbabwe",
  citizenship: "Zimbabwe",
  employment_status: "Employed",
  employer: null,
  gender: null,
  nationalId_number: null,
  phone: "",
  relationship_to_deceased: "",
  deceasedImageFile: null,
  special_request: null,
  notes: null,
  created_by: null,
  last_updated_by: null,
  update_history: [],
  booking_type: undefined,
  deceased_salutation: undefined,
  deceased_name: undefined,
  deceased_surname: undefined,
  deceased_dob: undefined,
  deceased_dod: undefined,
  date_of_service: undefined,
  start_time: undefined,
  end_time: undefined,
  service_address: undefined,
  service_city: undefined,
  church_name: undefined,
  leader_preacher_name: undefined,
  leader_preacher_surname: undefined,
  title: undefined,
  preacher_name: undefined,
  preacher_surname: undefined,
  venue: undefined,
  sub_title: undefined,
  hosts: undefined,
  host_surname: undefined,
  guests: undefined,
  guest_surname: undefined,
  location: undefined,
  company_name: undefined,
  branch_name: undefined,
  branch_rep: undefined,
  package: undefined,
  additional_services: [],
  additional_services_total: 0,
  package_total: 0,
  total: 0,
  address_one: "",
  address_two: "",
  city: "",
  conference_name: null,
  conference_type: null,
  end_date: null,
  audience_size: null,
  access_type: null,
  provide_platform: false,
  streaming_platform: null,
  record_stream: false,
  number_of_sessions: null,
  session_to_stream: null,
  specific_session: null,
  record_session: false,
  retention_duration: null,

  provide_on_demand_access: false,
  is_branding_needed: false,
  branding_requirements: null,
  interactive_features: [],
  interactive_needs: null,
  dedicated_space: false,
  internet_availability: false,
  internet_details: null,
  event_name: null,
  expected_audience_size: null,
  guest_of_honour: null,
  type_of_service: null,
  contact_id: null,
  contacts: [],
  representative_email: null,
  representative_phone: null,
  referral_partner_id: null,
  referral_partner_name: null,
};

export default AddBooking;
